import React, { useEffect, useState } from "react";
import SwitchIcon from "src/assets/images/switch.svg";
import BookIcon from "src/assets/images/book.svg";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { mapColumns } from "./action";
import AutoMapppedChat from "./AutoMapppedChat";
import DotLoader from "src/components/reusable/dot-loader";
import remarkGfm from "remark-gfm";
import Markdown from "react-markdown";
import rehypeExternalLinks from "rehype-external-links";

// -----------------------------
// Type Definitions
// -----------------------------
interface FieldMeta {
  label: string;
  signal_type: string;
  notes: string;
}

type FileMapping = {
  [columnName: string]: FieldMeta;
};

type MappingData = {
  [filename: string]: FileMapping;
};

type RawMappingEntry = {
  "Raw Column": string;
  "Detected Field": string;
  "Signal Type": string;
  "Notes": string;
};

type RawApiResponse = {
  fileName: string;
  columns: string[];
  mapping: string;
};

interface Props {
  files: File[];
  setMappedSelectedFile: (file: File) => void;
  mappedFile?: File;
}

const AutoMappedFields: React.FC<Props> = (props) => {
  const { files } = props;
  const dispatch = useAppDispatch();
  const rawPitchdeckSummary = useAppSelector((state) => state.VSProduct.pitchdeck_data?.pitchdeck_summary);
  const slideDocuments: { filename: string; content: any }[] = React.useMemo(() => {
    try {
      const parsed = JSON.parse(rawPitchdeckSummary);
      return parsed?.documents || [];
    } catch (err) {
      console.warn("Failed to parse pitchdeck_summary:", err);
      return [];
    }
  }, [rawPitchdeckSummary]);

  const [fieldMappingData, setFieldMappingData] = useState<MappingData>({});
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [open, setOpen] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFile(e.target.value);
    const matchedFile = files.find(f => f.name === e.target.value);
    if (matchedFile) {
      props.setMappedSelectedFile(matchedFile);
    }
  };

  const fields = fieldMappingData[selectedFile];

  function transformApiMappings(responses: RawApiResponse[]): MappingData {
    const mappingData: MappingData = {};

    for (const { fileName, mapping } of responses) {
      const parsed: RawMappingEntry[] = JSON.parse(mapping);
      const fileMapping: FileMapping = {};

      parsed.forEach(({ "Raw Column": rawCol, "Detected Field": label, "Signal Type": signal_type, "Notes": notes }) => {
        fileMapping[rawCol] = { label, signal_type, notes };
      });

      mappingData[fileName] = fileMapping;
    }

    const firstAvailable = Object.keys(mappingData)[0];
    const firstFile = files.find(f => f.name === firstAvailable);
    if (firstFile) {
      props.setMappedSelectedFile(firstFile);
    }

    return mappingData;
  }

  const uploadFile = async () => {
    setLoading(true);
    try {
      const results: RawApiResponse[] = [];

      for (const file of files) {
        try {
          const response = await dispatch(mapColumns({ file })).unwrap();
          results.push({
            fileName: file.name,
            ...response.data,
          });
        } catch (err) {
          console.warn(`Mapping failed for file: ${file.name}`, err);
          // skip this file so fallback rendering will take over
        }
      }
      setSelectedFile(files[0].name);
      const fieldMappingData = transformApiMappings(results);
      setFieldMappingData(fieldMappingData);
    } catch (err) {
      console.error("Unexpected error during upload:", err);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (files.length > 0) {
      uploadFile();
    }
  }, [files, dispatch]);

  return (
    <div
      className={`h-screen rounded-lg w-full overflow-hidden transition-all duration-300 ease-in-out ${open
        ? "flex-[0_0_100%] sm:flex-[0_0_75%] lg:flex-[0_0_460px] max-w-full lg:max-w-[460px]"
        : "flex-[0_0_100%] sm:flex-[0_0_215px] max-w-full sm:max-w-[215px]"
        }`}
    >
      <div className="flex flex-col h-full bg-opacity-50">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <img src={BookIcon} alt="Book Icon" className="w-3 h-3 object-contain" />
            <h2 className="text-lg font-semibold">Smart Field Detection & Signal Mapping</h2>
          </div>
          <button
            onClick={() => setOpen(!open)}
            className="cursor-pointer rounded border border-[#DFE3E8] p-1 hover:bg-gray-100"
            aria-label="Toggle Panel"
          >
            <img src={SwitchIcon} alt="Toggle" className="w-3 h-3 object-contain" />
          </button>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-500 animate-pulse">
            <DotLoader />
          </div>
        ) : (
          <div className="h-[55vh] overflow-y-auto pn_scroller flex flex-col">
            {/* File Select Dropdown */}
            {open && (
              <div className="mb-2">
                <label htmlFor="file-select" className="block text-sm font-medium text-gray-700 mb-1">
                  Select file to map
                </label>
                <select
                  id="file-select"
                  value={selectedFile}
                  onChange={handleChange}
                  className="block w-full px-2 py-1 border rounded-md border-gray-300 shadow-sm focus:outline-none focus:ring focus:ring-indigo-200"
                >
                  {files.map((file) => (
                    <option key={file.name} value={file.name}>
                      {file.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Table or Slide Content */}
            {(() => {
              const fields = fieldMappingData[selectedFile];
              const isMappingFailed =
                !fields || Object.values(fields)[0]?.label === "Mapping failed";

              if (isMappingFailed) {
                const doc = slideDocuments.find((d: any) => d.filename === selectedFile);
                return (
                  <div className="flex-1 flex flex-col justify-center items-center text-sm text-gray-500 px-4">

                    {doc?.content ? (
                      <Markdown
                        className="markdownWrapper text-secondary-800 text-justify text-align"
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[
                          [rehypeExternalLinks, { target: "_blank", rel: "noopener noreferrer" }],
                        ]}
                      >
                        {doc.content}
                      </Markdown>
                    ) : (
                      <p className="text-xs text-gray-400 mt-2">
                        No additional content found in parsed slides.
                      </p>
                    )}
                  </div>
                );
              }

              return (
                <div className="flex-1 overflow-auto pn_scroller">
                  <table className="min-w-full border border-[#DBDEEB] text-xs rounded-md">
                    <thead className="bg-[#F3F3F3] text-left sticky top-0 z-10 text-[#373D3F]">
                      <tr>
                        <th className="p-1 border border-[#DBDEEB] font-medium text-gray-700">Raw Column</th>
                        <th className="p-1 border border-[#DBDEEB] font-medium text-gray-700">Detected Field</th>
                        <th className="p-1 border border-[#DBDEEB] font-medium text-gray-700">Signal Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(fields).map(([rawColumn, meta]) => (
                        <tr
                          key={rawColumn}
                          className={meta.label === "Mapping failed" ? "bg-red-50 text-red-600" : "hover:bg-gray-50"}
                        >
                          <td className="p-1 border border-[#DBDEEB] font-mono">{rawColumn}</td>
                          <td className="p-1 border border-[#DBDEEB]">{meta.label}</td>
                          <td className="p-1 border border-[#DBDEEB]">{meta.signal_type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}

        {/* Chat Component */}
        {props.mappedFile && !loading && (
          <AutoMapppedChat file={props.mappedFile} />
        )}
      </div>
    </div>
  );

};

export default AutoMappedFields;
